import { defineStore } from 'pinia';
import {
	defaultPermissions,
	defaultProfile,
	defaultUser,
	userInfo2UserState,
	type UserPermissions,
	type UserProfile,
	type UserState,
	userState2Profile,
} from '../models/user';

import { type Auth, getAuth } from 'firebase/auth';
import { Firestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where } from 'firebase/firestore';
// import * as _ from 'lodash';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import first from 'lodash/first';
import clone from 'lodash/clone';
import { createApiToken, isTokenValid, type ApiToken } from '~/utils/apiToken';

export interface UserStateInterface {
	user?: UserState;
	profile?: UserProfile;
	permissions?: UserPermissions;
	apiToken?: ApiToken | null; // Local cache of token data from tokens collection
	openLoginDialog: boolean;
	openProfileDialog: boolean;
}

export const useUserStore = defineStore('UserStore', {
	state: (): UserStateInterface => ({
		user: undefined,
		profile: defaultProfile(),
		permissions: defaultPermissions(),
		apiToken: null,
		openLoginDialog: false,
		openProfileDialog: false,
	}),

	getters: {
		id(state: UserStateInterface): string {
			const id = get(state.user, 'uid') || '';
			return id;
		},
		alias(state: UserStateInterface): string {
			const alias = get(state.profile, 'alias') || '';
			if(alias == '') {
				return this.firstName;
			}
			return alias;
		},
		name(state: UserStateInterface): string {
			const name = get(state.user, 'displayName') || '';
			return name;
		},
		email(state: UserStateInterface): string {
			const email = get(state.user, 'email') || '';
			return email;
		},
		firstName(state: UserStateInterface): string {
			const name = get(state.user, 'displayName') || '';
			const firstName = first(name.split(' ')) || '';
			return firstName;
		},
		isLoggedIn(state: UserStateInterface): boolean {
			return !isNil(state.user?.uid) && this.isLoggedInToFirebase;
		},
		isAdmin(state: UserStateInterface): boolean {
			return (this.isLoggedIn && state.permissions?.admin) || false;
		},
		isBetaTester(state: UserStateInterface): boolean {
			return (this.isLoggedIn && state.permissions?.betaTester) || false;
		},
		isLoggedInToFirebase(state: UserStateInterface) {
			const user = getAuth().currentUser;
			return user?.uid !== null && user?.uid !== undefined;
		}
	},

	actions: {
		async login({elementId, uiConfig,}: {elementId: string | Element; uiConfig: firebaseui.auth.Config;}) {
			try {
				const firebaseui = await import('firebaseui');
				let ui = firebaseui.auth.AuthUI.getInstance();
				if (!ui) {
					ui = new firebaseui.auth.AuthUI(getAuth());
				}
				ui.start(elementId, uiConfig);
			} catch (error) {
				console.error('Failed to load FirebaseUI:', error);
				// Fallback: you might want to show a basic login form or error message
			}
		},
		async setUser(user_info: UserState) {
			if (!isNil(user_info)) {
				const storeUser = clone(user_info);
				this.openLoginDialog = false;
				this.updateUser(storeUser);
				await this.fetchUserProfile(storeUser);
				await this.fetchUserPermissions(storeUser);
			} else {
				this.updateUser(defaultUser());
			}
		},
		toggleLogin() {
			if (this.isLoggedIn) {
				this.logout();
			} else {
				this.openLoginDialog = true;
			}
		},
		toggleProfile() {
			this.openProfileDialog = !this.openProfileDialog;
		},
		async fetchUserProfile(user: UserState) {
			let profile = defaultProfile();
			if (user.uid) {
				const { $firestore } = useNuxtApp();
				const docRef = doc($firestore as Firestore, '/users', user.uid);
				const userProfile = await getDoc(docRef);
				if (!isNil(userProfile.data())) {
					profile = userProfile.data() as UserProfile;

					// Fetch token from tokens collection if tokenRef exists
					if (profile.tokenRef) {
						try {
							const tokenDoc = await getDoc(profile.tokenRef);
							if (tokenDoc.exists()) {
								this.apiToken = { ...tokenDoc.data(), token: tokenDoc.id } as ApiToken;
							}
						} catch (error) {
							console.error('Error fetching token from tokenRef:', error);
						}
					} else {
						// No tokenRef - check if user has a token in tokens collection
						const tokensQuery = query(
							collection($firestore as Firestore, 'tokens'),
							where('userId', '==', user.uid)
						);
						const tokensSnapshot = await getDocs(tokensQuery);

						if (!tokensSnapshot.empty) {
							// User has a token in tokens collection, update profile with tokenRef
							const tokenDoc = tokensSnapshot.docs[0];
							this.apiToken = { ...tokenDoc.data(), token: tokenDoc.id } as ApiToken;
							profile.tokenRef = tokenDoc.ref;

							// Update profile with tokenRef
							await setDoc(doc($firestore as Firestore, `/users`, profile.uid), profile)
								.catch((error:any) => {
									console.error('Error updating profile with tokenRef: ', error);
								});
						} else {
							// No token exists - generate new one for user
							await this.generateApiTokenForUser(profile);
						}
					}
				} else {
					const fbuser = getAuth().currentUser;
					if(fbuser?.uid != undefined) {
						user.uid = fbuser.uid;
						profile = userState2Profile(user);

						// Generate API token for new user
						await this.generateApiTokenForUser(profile);

						return await setDoc(doc($firestore as Firestore, `/users`, profile.uid), profile)
							.catch((error:any) => {
								console.error('Error saving Profile: ', error);
							});
					}
				}
				this.updateProfile(profile);
			}
		},
		async deleteProfile() {
			let profile = defaultProfile();
			if (this.user?.uid) {
				const { $firestore } = useNuxtApp();
				const docRef = doc($firestore as Firestore, '/users', this.user?.uid);
				const userProfile = await getDoc(docRef);
				if (!isNil(userProfile.data())) {
					profile = userProfile.data() as UserProfile;
					return await deleteDoc(doc($firestore as Firestore, `/users`, profile.uid))
					.catch((error:any) => {
						console.error('Error deleting profile: ', error);
					});	
				} 
				this.logout();
			}
		},
		// Permanently delete the account + all Cybercore data via the cascade
		// Cloud Function, then sign out. Copies others made from shares survive.
		async deleteAccount() {
			const { getFunctions, httpsCallable } = await import('firebase/functions');
			const del = httpsCallable(getFunctions(), 'deleteAccount');
			await del({});
			await this.logout();
		},

		async fetchUserPermissions(user: UserState) {
			let permissions = defaultPermissions();
			if (user.uid) {
				const { $firestore } = useNuxtApp();
				const docRef = doc($firestore as Firestore, `/users/${user?.uid}/permissions/`, 'rights');
				const userPermissions = await getDoc(docRef);
				if (!isNil(userPermissions.data())) {
					permissions = userPermissions.data() as UserPermissions;
				}
				this.updatePermissions(permissions);
			}
		},
		async logout() {
			this.updateUser(defaultUser());
			this.updateProfile(defaultProfile());
			const { $auth } : { $auth : Auth} = useNuxtApp() as any;
			await $auth.signOut();
			// await ($auth as Auth).signOut();
			// navigateTo('/');
		},
		updateUser(user: UserState) {
			this.user = clone(user);
		},
		updateProfile(profile: UserProfile) {
			this.profile = profile;
		},
		updatePermissions(permissions: UserPermissions) {
			this.permissions = permissions;
		},
		showLoginDialog() {
			this.openLoginDialog = true;
		},
		/**
		 * Helper to generate token for a user profile
		 */
		async generateApiTokenForUser(profile: UserProfile): Promise<ApiToken | null> {
			const { $firestore } = useNuxtApp();
			const userRef = doc($firestore as Firestore, `/users/${profile.uid}`);
			const token = createApiToken('API Access Token', profile.uid, userRef);

			// Save token to tokens collection (using token string as document ID)
			try {
				const tokenDocRef = doc($firestore as Firestore, `/tokens/${token.token}`);
				await setDoc(tokenDocRef, {
					id: token.id,
					name: token.name,
					userId: token.userId,
					userRef: token.userRef,
					createdAt: token.createdAt,
					expiresAt: token.expiresAt,
					lastUsed: token.lastUsed,
					active: token.active,
					requestCount: token.requestCount,
					lastRequestDate: token.lastRequestDate,
				});

				// Update profile with tokenRef
				profile.tokenRef = tokenDocRef;
				this.apiToken = token;

				console.log('API token generated successfully');
				return token;
			} catch (error) {
				console.error('Error generating API token: ', error);
				return null;
			}
		},
		/**
		 * Generate a new API token for the user
		 */
		async generateApiToken(): Promise<ApiToken | null> {
			if (!this.user?.uid || !this.profile) {
				console.error('User must be logged in to generate API token');
				return null;
			}

			// Check if user already has a revoked token
			const { $firestore } = useNuxtApp();
			const tokensQuery = query(
				collection($firestore as Firestore, 'tokens'),
				where('userId', '==', this.user.uid)
			);
			const tokensSnapshot = await getDocs(tokensQuery);

			// If user has a revoked token, they cannot generate a new one
			for (const tokenDoc of tokensSnapshot.docs) {
				const tokenData = tokenDoc.data() as ApiToken;
				if (!tokenData.active) {
					console.error('Cannot generate new token: user has a revoked token');
					return null;
				}
			}

			const token = await this.generateApiTokenForUser(this.profile);

			if (token) {
				// Update profile in Firestore
				try {
					await setDoc(doc($firestore as Firestore, `/users`, this.profile.uid), this.profile);
					return token;
				} catch (error) {
					console.error('Error updating profile: ', error);
					return null;
				}
			}

			return null;
		},
		/**
		 * Revoke/delete the current API token
		 * Once revoked, user cannot generate a new token
		 */
		async revokeApiToken(): Promise<boolean> {
			if (!this.user?.uid || !this.profile || !this.apiToken) {
				console.error('User must be logged in with a token to revoke it');
				return false;
			}

			const { $firestore } = useNuxtApp();
			const tokenString = this.apiToken.token;

			// Mark token as inactive in tokens collection
			try {
				await setDoc(doc($firestore as Firestore, `/tokens/${tokenString}`), {
					...this.apiToken,
					active: false,
				});

				// Update local state to show token is revoked
				this.apiToken.active = false;

				console.log('API token revoked successfully');
				return true;
			} catch (error) {
				console.error('Error revoking API token: ', error);
				return false;
			}
		},
		/**
		 * Refresh/regenerate the API token
		 * Deletes old token and creates a new one
		 */
		async refreshApiToken(): Promise<ApiToken | null> {
			if (!this.user?.uid || !this.profile || !this.apiToken) {
				console.error('User must be logged in with a token to refresh it');
				return null;
			}

			const { $firestore } = useNuxtApp();
			const oldTokenString = this.apiToken.token;

			// Delete old token from tokens collection
			try {
				await deleteDoc(doc($firestore as Firestore, `/tokens/${oldTokenString}`));
			} catch (error) {
				console.error('Error deleting old token: ', error);
			}

			// Generate new token
			const newToken = await this.generateApiToken();

			// Update profile with new tokenRef
			if (newToken) {
				await setDoc(doc($firestore as Firestore, `/users`, this.profile.uid), this.profile);
			}

			return newToken;
		},
	},
});
